// Pomii și boscheții machetei de situație, ca în planșele de prezentare de
// arhitectură: trunchi subțire care se ramifică, coroana din PLĂCI cu textură de
// frunziș (desenată în cod, cu goluri prin care trece lumina), umbre cu goluri.
//
// Costul nu e în triunghiuri (~470 pe pom, ~60 pe boschet), ci în pixelii
// plăcilor: fiecare e testat pe transparență, iar frunzișul se suprapune. De
// aceea plăcile sunt puține și mari. Câteva variante de pom și de boschet,
// fiecare desenată o dată și instanțiată: sute de pomi, câteva apeluri de desen.
//
// Totul e determinist: varianta, rotația, mărimea și nuanța ies din poziție.
// Nuanțele variază FOARTE PUȚIN în jurul aceluiași verde — un rând de pomi se
// citește ca o plantație, nu ca o paletă.
//
// Poziția și raza coroanei vin din `scatterTrees` — aceeași plantație ca pe
// planșa 2D; boscheții din `vegetatie.ts`. Aici se decide doar CUM arată.
import * as THREE from "three";
import * as BGU from "three/examples/jsm/utils/BufferGeometryUtils.js";

export interface CopacSit { x: number; y: number; r: number }
/** Un boschet: centrul și raza lui, în metri. */
export interface TufisSit { x: number; y: number; r: number }

// --------------------------------------------------------------------------- //
// zaruri deterministe
// --------------------------------------------------------------------------- //

function hash3(i: number, j: number, k: number, s: number): number {
  let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1) ^ Math.imul(k, 0x9e3779b1) ^ s;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/** Un număr stabil în [0, 1) pentru un pom: din poziția lui, rotunjită la 5 cm. */
export function hashPom(x: number, y: number, sare = 0): number {
  return hash3(Math.round(x * 20), Math.round(y * 20), 0x51ed, sare);
}

/** Un șir de numere stabile în [0, 1) (mulberry32). */
function zar(sare: number) {
  let a = sare >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --------------------------------------------------------------------------- //
// frunzișul
// --------------------------------------------------------------------------- //

/** Verdele pomilor: verde-gălbui și oliv, deschis. Boscheții, puțin mai închiși. */
const TONURI_POM = ["#7d8e45", "#8d9d55", "#9aa955", "#a9b267", "#b7bd6c", "#6f8343"];
const TONURI_TUFIS = ["#62743a", "#6f8343", "#7d8e45", "#8d9d55", "#56682f"];

const texturi = new Map<string, THREE.CanvasTexture>();

/** Frunzișul unei plăci: sute de frunze mici, în tonurile date, adunate în
 *  ciorchini neregulați cu goluri, pe fond transparent. Ținută o dată pe
 *  pagină: scenele se refac, textura rămâne. */
function texturaFrunzis(cheie: string, sare: number, tonuri: string[], ciorchini: number,
                        frunze: number): THREE.CanvasTexture {
  const tinuta = texturi.get(cheie);
  if (tinuta) return tinuta;
  const N = 256;
  const c = document.createElement("canvas");
  c.width = c.height = N;
  const x = c.getContext("2d");
  if (x) {
    const r = zar(sare);
    const nori = Array.from({ length: ciorchini }, () => ({
      cx: N * (0.2 + r() * 0.6), cy: N * (0.18 + r() * 0.6), rr: N * (0.1 + r() * 0.12),
    }));
    for (let k = 0; k < frunze; k++) {
      const ci = nori[Math.floor(r() * nori.length)];
      const a = r() * Math.PI * 2, d = Math.sqrt(r()) * ci.rr;
      const px = ci.cx + Math.cos(a) * d, py = ci.cy + Math.sin(a) * d;
      // mai deschis sus, unde bate lumina
      const lum = 0.82 + (1 - py / N) * 0.3 + (r() - 0.5) * 0.12;
      x.save();
      x.translate(px, py);
      x.rotate(r() * Math.PI);
      x.filter = `brightness(${lum.toFixed(2)})`;
      x.fillStyle = tonuri[Math.floor(r() * tonuri.length)];
      x.beginPath();
      x.ellipse(0, 0, 3.2 + r() * 3.2, 1.6 + r() * 1.6, 0, 0, Math.PI * 2);
      x.fill();
      x.restore();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  texturi.set(cheie, t);
  return t;
}

/** Plăcile unui nor de frunziș: în jurul fiecărui punct, `placiPePunct` plăci
 *  încrucișate de latură `s`, aproape verticale, și un CAPAC aproape orizontal
 *  — fără el, privit de sus (planul de situație), un pom era o stea de lame
 *  subțiri, nu o coroană. Normala fiecărui colț pleacă din `centru`, ca lumina
 *  să cadă pe frunziș ca pe un volum, nu pe plăci plate. */
function placi(puncte: { p: THREE.Vector3; s: number }[], centru: THREE.Vector3, placiPePunct: number,
               r: () => number): THREE.BufferGeometry {
  const sus = new THREE.Vector3(0, 0, 1);
  const poz: number[] = [], norm: number[] = [], uv: number[] = [], col: number[] = [];
  const colt = [[-1, -0.7], [1, -0.7], [1, 1.1], [-1, 1.1]];
  const uvs = [[0, 0], [1, 0], [1, 1], [0, 1]];
  const adauga = (pts: THREE.Vector3[], ton: number) => {
    for (const i of [0, 1, 2, 0, 2, 3]) {
      const p = pts[i];
      poz.push(p.x, p.y, p.z);
      const n = p.clone().sub(centru).normalize().lerp(sus, 0.35).normalize();
      norm.push(n.x, n.y, n.z);
      uv.push(uvs[i][0], uvs[i][1]);
      col.push(ton, ton, ton);
    }
  };
  for (const { p: v, s } of puncte) {
    // o nuanță pe ciorchine, foarte apropiată de celelalte
    const ton = 0.94 + r() * 0.12;
    for (let q = 0; q < placiPePunct; q++) {
      const az = r() * Math.PI + (q * Math.PI) / placiPePunct;
      const ex = new THREE.Vector3(Math.cos(az), Math.sin(az), 0).multiplyScalar(s);
      const ez = new THREE.Vector3((r() - 0.5) * 0.3, (r() - 0.5) * 0.3, 1).normalize().multiplyScalar(s);
      const pts = colt.map(([a, b]) => v.clone().addScaledVector(ex, a).addScaledVector(ez, b * 0.85));
      adauga(pts, ton);
    }
    // capacul: în planul orizontal, puțin aplecat; mai mic decât plăcile, ca
    // pe diagonală să nu lățească coroana peste amprenta ei
    const az = r() * Math.PI * 2;
    const ex = new THREE.Vector3(Math.cos(az), Math.sin(az), (r() - 0.5) * 0.25).normalize().multiplyScalar(s * 0.72);
    const ey = new THREE.Vector3(-Math.sin(az), Math.cos(az), (r() - 0.5) * 0.25).normalize().multiplyScalar(s * 0.72);
    const c0 = v.clone().addScaledVector(sus, s * 0.15);
    adauga([[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => c0.clone().addScaledVector(ex, a).addScaledVector(ey, b)),
           ton);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(norm, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  return g;
}

// --------------------------------------------------------------------------- //
// pomul
// --------------------------------------------------------------------------- //

/** Cât de înalt e pomul, în raze de coroană, pe variantă; și cât de lată e
 *  coroana față de amprenta ei de pe planșă. */
const VARIANTE_POM = [
  { h: 2.7, lat: 1.0, sare: 101 },
  { h: 3.2, lat: 0.9, sare: 118 },
  { h: 2.5, lat: 1.05, sare: 135 },
  { h: 2.9, lat: 0.95, sare: 152 },
] as const;

export type VariantaPom = 0 | 1 | 2 | 3;
export const VARIANTE: VariantaPom[] = [0, 1, 2, 3];

/** Varianta unui pom, stabilă, din poziția lui. */
export function variantaPom(c: CopacSit): VariantaPom {
  return Math.floor(hashPom(c.x, c.y, 1) * VARIANTE_POM.length) as VariantaPom;
}

interface Ramura { a: THREE.Vector3; b: THREE.Vector3; r: number }

/** Un pom pentru raza de coroană 1 (amprenta de pe planșă), baza la (0, 0, 0):
 *  lemnul (trunchi și ramuri) și frunzișul (plăci). */
export function geometriePom(v: VariantaPom): { lemn: THREE.BufferGeometry; frunze: THREE.BufferGeometry } {
  const { h, lat, sare } = VARIANTE_POM[v];
  const r = zar(sare);
  const ramuri: Ramura[] = [];
  const varfuri: THREE.Vector3[] = [];
  const sus = new THREE.Vector3(0, 0, 1);
  const rTrunchi = 0.06;
  const bazaCoroana = h * 0.42;
  ramuri.push({ a: new THREE.Vector3(0, 0, -0.02), b: new THREE.Vector3(0, 0, bazaCoroana), r: rTrunchi });

  const creste = (din: THREE.Vector3, dir: THREE.Vector3, L: number, raza: number, nivel: number) => {
    const b = din.clone().addScaledVector(dir, L);
    ramuri.push({ a: din.clone(), b, r: raza });
    if (nivel >= 2) { varfuri.push(b); return; }
    const n = nivel === 0 ? 2 : 2 + Math.floor(r() * 2);
    for (let k = 0; k < n; k++) {
      const start = din.clone().lerp(b, 0.55 + r() * 0.45);
      const az = r() * Math.PI * 2;
      const axa = new THREE.Vector3(Math.cos(az), Math.sin(az), 0).cross(dir).normalize();
      const d = dir.clone().applyAxisAngle(axa, 0.35 + r() * 0.5).lerp(sus, 0.15).normalize();
      creste(start, d, L * (0.55 + r() * 0.2), raza * 0.6, nivel + 1);
    }
    if (nivel === 1) varfuri.push(b);
  };
  // 4–5 ramuri principale din vârful trunchiului, plus conducătorul
  const nPrincipale = 4 + Math.floor(r() * 2);
  for (let k = 0; k < nPrincipale; k++) {
    const az = (k / nPrincipale) * Math.PI * 2 + r() * 0.6;
    const ap = 0.55 + r() * 0.35;
    const dir = new THREE.Vector3(Math.sin(ap) * Math.cos(az), Math.sin(ap) * Math.sin(az), Math.cos(ap));
    creste(new THREE.Vector3(0, 0, bazaCoroana * (0.85 + r() * 0.15)), dir,
           lat * (0.6 + r() * 0.2), rTrunchi * 0.55, 0);
  }
  creste(new THREE.Vector3(0, 0, bazaCoroana), sus.clone(), (h - bazaCoroana) * 0.55, rTrunchi * 0.6, 1);

  const parti: THREE.BufferGeometry[] = [];
  for (const rm of ramuri) {
    const L = rm.a.distanceTo(rm.b);
    if (L < 1e-3) continue;
    const g = new THREE.CylinderGeometry(rm.r * 0.7, rm.r, L, 5, 1, true);
    g.translate(0, L / 2, 0);
    g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0),
      rm.b.clone().sub(rm.a).normalize()));
    g.translate(rm.a.x, rm.a.y, rm.a.z);
    g.deleteAttribute("uv");
    parti.push(g);
  }
  const lemn = BGU.mergeGeometries(parti, false)!;
  parti.forEach((g) => g.dispose());

  const centru = new THREE.Vector3(0, 0, bazaCoroana + (h - bazaCoroana) * 0.5);
  const frunze = placi(varfuri.map((p) => ({ p, s: lat * (0.55 + r() * 0.3) })), centru, 2, r);
  // COROANA CÂT CERCUL DE PE PLANȘĂ: plăcile ies cu colțurile peste ramuri, iar
  // nestrânsă coroana ajungea la 1,6–1,8 din raza planșei — un pom pus la
  // distanță de clădire pe plan își băga coroana în fațadă pe machetă. Se
  // strânge pe orizontală (lemn și frunziș deodată) până când 90 % din
  // frunziș stă în raza 1; colțurile plăcilor, transparente, trec puțin peste.
  const p = frunze.attributes.position as THREE.BufferAttribute;
  const raze: number[] = [];
  for (let i = 0; i < p.count; i++) raze.push(Math.hypot(p.getX(i), p.getY(i)));
  raze.sort((a, b) => a - b);
  const k = 1 / Math.max(0.5, raze[Math.floor(raze.length * 0.9)] ?? 1);
  const strange = new THREE.Matrix4().makeScale(k, k, 1);
  frunze.applyMatrix4(strange);
  lemn.applyMatrix4(strange);
  return { lemn, frunze };
}

// --------------------------------------------------------------------------- //
// boschetul
// --------------------------------------------------------------------------- //

const VARIANTE_TUFIS = [211, 229, 247] as const;

/** Un boschet pentru raza 1, baza la (0, 0, 0): o cupolă joasă de frunziș, fără
 *  lemn la vedere — câțiva ciorchini pe un inel și unul în mijloc. */
export function geometrieTufis(v: number): THREE.BufferGeometry {
  const r = zar(VARIANTE_TUFIS[v % VARIANTE_TUFIS.length]);
  const pts: { p: THREE.Vector3; s: number }[] = [{ p: new THREE.Vector3(0, 0, 0.45), s: 0.7 }];
  const n = 6 + Math.floor(r() * 2);
  for (let k = 0; k < n; k++) {
    const a = (k / n) * Math.PI * 2 + r() * 0.5;
    const d = 0.42 + r() * 0.2;
    pts.push({ p: new THREE.Vector3(Math.cos(a) * d, Math.sin(a) * d, 0.25 + r() * 0.2),
               s: 0.5 + r() * 0.18 });
  }
  // trei plăci pe ciorchine: de oriunde l-ai privi, boschetul e plin
  return placi(pts, new THREE.Vector3(0, 0, 0.1), 3, r);
}

// --------------------------------------------------------------------------- //
// instanțiere
// --------------------------------------------------------------------------- //

export interface PomiMachetei {
  obiecte: THREE.Object3D[];
  deArs: { dispose(): void }[];
  /** Câți pomi pe variantă și câți boscheți — pentru probe. */
  peVariante: Record<VariantaPom, number>;
  tufisuri: number;
}

const fara = () => { /* vegetația nu se alege cu mouse-ul */ };

/** Nuanța unui pom sau boschet: foarte aproape de 1 pe toate canalele — un
 *  pic mai deschis sau mai închis, un pic mai cald sau mai rece. */
function nuanta(x: number, y: number, tinta: THREE.Color): THREE.Color {
  const l = 1 + (hashPom(x, y, 4) - 0.5) * 0.1;
  const cald = (hashPom(x, y, 5) - 0.5) * 0.05;
  return tinta.setRGB(l * (1 + cald), l, l * (1 - cald));
}

function materialFrunze(map: THREE.Texture): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    map, alphaTest: 0.45, side: THREE.DoubleSide, vertexColors: true, roughness: 0.9,
    alphaToCoverage: true,
  });
}

/** Toți pomii și boscheții scenei: câte o plasă instanțiată pe variantă de
 *  pom (lemn + frunziș) și pe variantă de boschet. `umbre: false` pentru
 *  vegetația orașului, care nu aruncă umbre (v. `oras/strat3d.ts`). */
export function construiestePomi(copaci: CopacSit[], tufisuri: TufisSit[] = [],
                                 { umbre = true }: { umbre?: boolean } = {}): PomiMachetei {
  const deArs: { dispose(): void }[] = [];
  const obiecte: THREE.Object3D[] = [];
  const peVariante = { 0: 0, 1: 0, 2: 0, 3: 0 } as Record<VariantaPom, number>;
  const t = new THREE.Object3D();
  const tint = new THREE.Color();

  const matLemn = new THREE.MeshStandardMaterial({ color: 0x6e6052, roughness: 1 });
  const matPom = materialFrunze(texturaFrunzis("pom", 7, TONURI_POM, 9, 900));
  // boschetul e frunziș des: mai multe frunze, în mai mulți ciorchini
  const matTufis = materialFrunze(texturaFrunzis("tufis", 19, TONURI_TUFIS, 16, 2200));
  deArs.push(matLemn, matPom, matTufis);

  const grupe = new Map<VariantaPom, CopacSit[]>();
  for (const c of copaci) {
    const v = variantaPom(c);
    peVariante[v]++;
    (grupe.get(v) ?? grupe.set(v, []).get(v)!).push(c);
  }
  for (const [v, lista] of grupe) {
    const g = geometriePom(v);
    deArs.push(g.lemn, g.frunze);
    const lemn = new THREE.InstancedMesh(g.lemn, matLemn, lista.length);
    const frunze = new THREE.InstancedMesh(g.frunze, matPom, lista.length);
    lemn.castShadow = frunze.castShadow = umbre;
    frunze.receiveShadow = umbre;
    lemn.raycast = fara; frunze.raycast = fara;
    lemn.name = `pomi:${v}:lemn`; frunze.name = `pomi:${v}:frunze`;
    lista.forEach((c, i) => {
      // mărimea pomului = amprenta lui de pe planșă, cu ±6 %
      const sc = c.r * (0.94 + hashPom(c.x, c.y, 3) * 0.12);
      t.position.set(c.x, c.y, -0.05);           // 5 cm în pământ, nu pe el
      t.rotation.set(0, 0, hashPom(c.x, c.y, 2) * Math.PI * 2);
      t.scale.set(sc, sc, sc);
      t.updateMatrix();
      lemn.setMatrixAt(i, t.matrix);
      frunze.setMatrixAt(i, t.matrix);
      frunze.setColorAt(i, nuanta(c.x, c.y, tint));
    });
    lemn.instanceMatrix.needsUpdate = frunze.instanceMatrix.needsUpdate = true;
    if (frunze.instanceColor) frunze.instanceColor.needsUpdate = true;
    obiecte.push(lemn, frunze);
  }

  const grupeT = new Map<number, TufisSit[]>();
  for (const b of tufisuri) {
    const v = Math.floor(hashPom(b.x, b.y, 7) * VARIANTE_TUFIS.length);
    (grupeT.get(v) ?? grupeT.set(v, []).get(v)!).push(b);
  }
  for (const [v, lista] of grupeT) {
    const g = geometrieTufis(v);
    deArs.push(g);
    const m = new THREE.InstancedMesh(g, matTufis, lista.length);
    m.castShadow = m.receiveShadow = umbre;
    m.raycast = fara;
    m.name = `tufisuri:${v}`;
    lista.forEach((b, i) => {
      t.position.set(b.x, b.y, -0.03);
      t.rotation.set(0, 0, hashPom(b.x, b.y, 8) * Math.PI * 2);
      // boschetul e mai lat decât înalt
      t.scale.set(b.r, b.r, b.r * (0.75 + hashPom(b.x, b.y, 9) * 0.3));
      t.updateMatrix();
      m.setMatrixAt(i, t.matrix);
      m.setColorAt(i, nuanta(b.x, b.y, tint));
    });
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    obiecte.push(m);
  }
  return { obiecte, deArs, peVariante, tufisuri: tufisuri.length };
}
